import React, { useCallback } from "react"
import { SetterOrUpdater } from "recoil"
import { BaseVisualizationConfigureModal } from "./BaseVisualizationConfigureModal"
import { ElementConfigureDiv } from "../../../../../../Constants/StyledComponents"
import { EEG_WINDOW_TIME_PRESETS } from "../../../../../../Managers/VisualizationManager/Viewport/Components/XAxis"
import { EEGMontageConfigJSON } from "../../Types/EEGMontageConfig"
import { eegMontageConfigAtom } from "../../Atoms/EEGMontage"
import { ECG_SENSITIVITY, EEG_HIGH_FILTERS, EEG_LOW_FILTERS, EEG_NOTCH_FILTERS, EEG_SENSITIVITY, montageHasECGChannel } from "../../Types/EEG"
import { useEditWindow } from "./useEditWindow"
import { MobergDropdown, MobergDropdownSize } from "../../../../../../Components/MobergDropdown/MobergDropdown"
import { DataReviewEEGMontageDropdown } from "../../../../../../Components/EEGMontageDropdownMenu/DataReviewEEGMontageDropdown"
import { MobergText } from "../../../../../../Components/MobergHeader/MobergHeader"
import { MobergFontSize } from "../../../../../../Components/MobergFont/MobergFont"
import { useEEGCalibration } from "../../Calibration/hooks"
import { EEGCalibrationStatus } from "../../Calibration/types"

type ConfigureEEGModalProps = {
	layoutId: string
	windowId: string
	escClose?: boolean
	clickOutsideClose?: boolean
}

export const ConfigureEEGModal = (props: ConfigureEEGModalProps) => {
	const atom = eegMontageConfigAtom({ windowId: props.windowId, layoutId: props.layoutId })
	const { editedConfig, saveChanges, updateProperty, updateProps } = useEditWindow<EEGMontageConfigJSON>({ recoilState: atom, windowId: props.windowId, layoutId: props.layoutId })
	const { status: calibrationStatus, pxFromMm } = useEEGCalibration()
	const montageHasECG = montageHasECGChannel(editedConfig.montage)

	// This setter updates the props on the layout and works just like a react setState hook.
	const setEEGConfig: SetterOrUpdater<EEGMontageConfigJSON> = useCallback(newValueOrSetter => {
		if (newValueOrSetter instanceof Function) {
			updateProps(newValueOrSetter(editedConfig))
		} else {
			updateProps(newValueOrSetter)
		}
	}, [editedConfig, updateProps])

	return (
		<BaseVisualizationConfigureModal
			layoutId={props.layoutId}
			windowId={props.windowId}
			escClose={props.escClose}
			clickOutsideClose={props.clickOutsideClose}
			saveChanges={saveChanges}
			title={"Configure EEG Montage"}
		>
			<div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
				<div style={{ padding: "20px", width: "450px" }}>

					<ElementConfigureDiv>
						Montage

						<DataReviewEEGMontageDropdown 
							size={MobergDropdownSize.REGULAR}
							placement={"right-start"}
							currentMontage={editedConfig.montage}
							updateCurrentMontage={montage => setEEGConfig(previous => ({...previous, montage }))}
						/>
					</ElementConfigureDiv>

					<ElementConfigureDiv>
						Initial Page Size

						<MobergDropdown
							selectedValue={editedConfig?.viewDuration}
							onChange={value => updateProperty("viewDuration", parseInt(value as string))}
							options={EEG_WINDOW_TIME_PRESETS
								.filter(preset => preset.label !== "Page Size")
								.map(preset => ({ label: preset.label, value: preset.time }))}
						/>
					</ElementConfigureDiv>

					<ElementConfigureDiv>
						LFF

						<MobergDropdown
							selectedValue={editedConfig?.LFF}
							onChange={value => updateProperty("LFF", parseFloat(value as string))}
							options={Object.entries(EEG_LOW_FILTERS).map(([label, value]) => ({ label, value }))}
						/>
					</ElementConfigureDiv>

					<ElementConfigureDiv>
						HFF

						<MobergDropdown
							selectedValue={editedConfig?.HFF}
							onChange={value => updateProperty("HFF", parseFloat(value as string))}
							options={Object.entries(EEG_HIGH_FILTERS).map(([label, value]) => ({ label, value }))}
						/>
					</ElementConfigureDiv>

					<ElementConfigureDiv>
						Notch Filter Frequency

						<MobergDropdown
							selectedValue={editedConfig?.notch}
							onChange={value => updateProperty("notch", parseFloat(value as string))}
							options={Object.entries(EEG_NOTCH_FILTERS).map(([label, value]) => ({ label, value }))}
						/>
					</ElementConfigureDiv>

					<ElementConfigureDiv>
						<div>
							Sensitivity
							{calibrationStatus === EEGCalibrationStatus.VALID && (
								<MobergText
									fontSize={MobergFontSize.SMALL}
									style={{ color: "var(--text-secondary-color)", marginTop: "4px", display: "block", paddingRight: "16px" }}
								>
									1 mm is approx. {pxFromMm(1).toFixed(1)} pixels<br />
									±100 uV spans {(200 / (editedConfig.sensitivityMicroVolts * 1e6)).toFixed(1)} mm peak-to-peak
								</MobergText>
							)}
						</div>
						<MobergDropdown
							selectedValue={editedConfig?.sensitivityMicroVolts}
							onChange={value => updateProperty("sensitivityMicroVolts", parseFloat(value as string))}
							options={Object.entries(EEG_SENSITIVITY).map(([label, value]) => ({ label, value }))}
						/>
					</ElementConfigureDiv>

					{montageHasECG && (
						<ElementConfigureDiv>
							ECG Sensitivity

							<MobergDropdown
								selectedValue={editedConfig?.ecgSensitivity ?? ECG_SENSITIVITY["7 mm/mV"]}
								onChange={value => updateProperty("ecgSensitivity", parseFloat(value as string))}
								options={Object.entries(ECG_SENSITIVITY).map(([label, value]) => ({ label, value }))}
							/>
						</ElementConfigureDiv>
					)}
				</div>
			</div>
		</BaseVisualizationConfigureModal>
	)
}
